import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchAllEvents from 'src/Hooks/Queries/useFetchAllEvents';
import useFetchCartographyTree from 'src/Hooks/Queries/useFetchCartographyTree';
import useFetchCommunityFeedMemories from 'src/Hooks/Queries/useFetchCommunityFeedMemories';
import useFetchContacts from 'src/Hooks/Queries/useFetchContacts';
import useFetchExpenses from 'src/Hooks/Queries/useFetchExpenses';
import useFetchInventory from 'src/Hooks/Queries/useFetchInventory';
import useFetchProjects from 'src/Hooks/Queries/useFetchProjects';
import useFetchTasks from 'src/Hooks/Queries/useFetchTasks';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';

export interface LinkableSearchResult {
  id: string;
  title: string;
  subtitle?: string;
}

// Accent- and case-insensitive: "cuisine" finds "Cuisine", "ete" finds "L'été sans cuisine".
const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const matchesQuery = (title: string, query: string) =>
  query.trim().length === 0 || normalize(title).includes(normalize(query.trim()));

/**
 * Searches across the linkable entity types. All underlying list hooks are
 * called unconditionally (rules of hooks — entityType can change while the modal's
 * tabs are open) and each already has its own `enabled: !!assetId` guard, so this only
 * costs an extra cache lookup for the inactive tabs, not extra requests once cached.
 * VirtualFile has no case here on purpose — excluded from the MVP picker (search
 * endpoint not available, see BRIEF-VFS-SEARCH / V2).
 */
const useSearchLinkableEntities = (
  entityType: LinkableEntityType,
  query: string,
  excludeIds: string[]
): { results: LinkableSearchResult[]; isLoading: boolean } => {
  const { t, i18n } = useTranslation();
  const assetId = useCurrentAssetId();

  const rooms = useFetchCartographyTree();
  const events = useFetchAllEvents();
  const memories = useFetchCommunityFeedMemories();
  const inventory = useFetchInventory();

  // Memories are paginated by 10: load them all once the tab is opened, a family rarely has
  // more than a few dozen albums.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = memories;
  useEffect(() => {
    if (entityType === LinkableEntityType.CommunityFeedItem && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [entityType, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const tasks = useFetchTasks(assetId ?? '');
  const projects = useFetchProjects(assetId ?? '');
  const contacts = useFetchContacts();
  const expenses = useFetchExpenses();

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  return useMemo(() => {
    switch (entityType) {
      case LinkableEntityType.Task:
        return {
          isLoading: tasks.isLoading,
          results: (tasks.data ?? [])
            .filter(task => !excludeSet.has(task.id) && matchesQuery(task.title, query))
            .map(task => ({ id: task.id, title: task.title })),
        };
      case LinkableEntityType.Project:
        return {
          isLoading: projects.isLoading,
          results: (projects.data ?? [])
            .filter(project => !excludeSet.has(project.id) && matchesQuery(project.title, query))
            .map(project => ({ id: project.id, title: project.title })),
        };
      case LinkableEntityType.Contact:
        return {
          isLoading: contacts.isLoading,
          results: (contacts.data ?? [])
            .map(contact => ({
              id: contact.id,
              title: contact.displayName,
            }))
            .filter(contact => !excludeSet.has(contact.id) && matchesQuery(contact.title, query)),
        };
      case LinkableEntityType.Expense:
        return {
          isLoading: expenses.isLoading,
          results: expenses.data
            .filter(expense => !excludeSet.has(expense.id) && matchesQuery(expense.title, query))
            .map(expense => ({
              id: expense.id,
              title: expense.title,
              subtitle: `${expense.amount.toLocaleString('fr-FR')} €`,
            })),
        };
      case LinkableEntityType.Room:
        return {
          isLoading: rooms.isLoading,
          results: (rooms.data?.buildings ?? []).flatMap(building =>
            building.rooms
              .filter(room => !excludeSet.has(room.id) && matchesQuery(room.name, query))
              .map(room => ({
                id: room.id,
                title: room.name,
                subtitle:
                  [
                    building.floors.find(floor => floor.id === room.floorId)?.name,
                    rooms.data && rooms.data.buildings.length > 1 ? building.name : undefined,
                  ]
                    .filter(Boolean)
                    .join(' · ') || undefined,
              }))
          ),
        };
      case LinkableEntityType.Event:
        return {
          isLoading: events.isLoading,
          results: (events.data ?? [])
            .filter(event => !excludeSet.has(event.id) && matchesQuery(event.name, query))
            .map(event => ({ id: event.id, title: event.name })),
        };
      case LinkableEntityType.CommunityFeedItem: {
        const langKey = i18n.language.split('-')[0] as 'en' | 'fr';
        return {
          isLoading: memories.isLoading,
          results: (memories.data?.pages.flatMap(page => page.items) ?? [])
            .map(memory => ({
              id: memory.id,
              title:
                (memory.content[langKey] ?? memory.content.en ?? '').trim() ||
                t('community.albumFallback', 'Album du {{date}}', {
                  date: dayjs(memory.createdAt).format('DD/MM/YYYY'),
                }),
            }))
            .filter(memory => !excludeSet.has(memory.id) && matchesQuery(memory.title, query)),
        };
      }
      case LinkableEntityType.InventoryItem:
        return {
          isLoading: inventory.isLoading,
          results: (inventory.data?.items ?? [])
            .filter(item => !excludeSet.has(item.id) && matchesQuery(item.name, query))
            .map(item => ({ id: item.id, title: item.name, subtitle: item.room?.name })),
        };
      case LinkableEntityType.VirtualFile:
        // Not searchable in MVP — see BRIEF-VFS-SEARCH (V2).
        return { isLoading: false, results: [] };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entityType,
    query,
    excludeSet,
    tasks.data,
    projects.data,
    contacts.data,
    expenses.data,
    rooms.data,
    events.data,
    memories.data,
  ]);
};

export default useSearchLinkableEntities;
